"use client";

import { useEffect } from "react";
import { differenceInDays, parseISO } from "date-fns";
import { useSearchParams } from "next/navigation";
import { MAP_AREA } from "@/constant";
import { PrecisionPricingStatusType } from "commonModule/services/booking/types";
import { PropertyTypeV2 } from "commonModule/services/common";
import { CLIENT_PREVIEW_VALUE, PREVIEW_PARAM } from "hooks/previewMode";
import { markBookingStore } from "stores/booking/bookingSlice";
import { useAppDispatch } from "stores/hooks";
import { markSearch } from "stores/search/searchSlice";
import { canUseDOM } from "utils/dom";
import { getRefTaPropertyId, getRefTaToken, setRefTaPropertyId, setRefTaToken } from "utils/local-storage";
import { shouldBindRefTaProperty } from "utils/refTaBinding";
import { dashedToCommaSeparatedName, ensembleCityStateCountry, formatDateYYYYMMDD } from "utils/url";
const DetailPageInitiateLogic = ({
  property
}:
// precisionPricingStatus,
{
  property: PropertyTypeV2;
  precisionPricingStatus?: PrecisionPricingStatusType | null;
}) => {
  const dispatch = useAppDispatch();
  const params = useSearchParams();

  // set precision pricing data
  // useEffect(() => {
  //   if (precisionPricingStatus) {
  //     dispatch(
  //       setPrecisionPrincingData({
  //         ...precisionPricingStatus,
  //       }),
  //     )
  //   }
  // }, [precisionPricingStatus, dispatch])

  useEffect(() => {
    if (!canUseDOM()) return;
    // Skip persisting the referral token when an agent is previewing the
    // page — preview should not mutate their real session.
    if (params?.get(PREVIEW_PARAM) === CLIENT_PREVIEW_VALUE) return;
    // Capture ref_ta token from URL for travel agent referral tracking
    const refTa = params?.get("ref_ta");
    if (refTa) {
      // Bind the property only for a genuinely new referral. The same token
      // re-appended by buildDetailPageUrl on another property must NOT rebind,
      // or pricing would leak to every browsed property. Read the stored token
      // BEFORE overwriting it below.
      if (shouldBindRefTaProperty(refTa, getRefTaToken(), getRefTaPropertyId())) {
        setRefTaPropertyId(property.property_id);
      }
      setRefTaToken(refTa);
    }
  }, [params, property.property_id]);
  useEffect(() => {
    if (canUseDOM()) {
      const searchParams = new URLSearchParams(window.location.search);
      const checkIn = searchParams.get("check_in");
      const checkOut = searchParams.get("check_out");
      const commissionRateFromUrl = searchParams.get("commissionRatePercent");
      const parsedCommissionRate = Number(commissionRateFromUrl);
      const commissionRatePercent = commissionRateFromUrl != null && Number.isFinite(parsedCommissionRate) && parsedCommissionRate >= 0 && parsedCommissionRate <= 100 ? parsedCommissionRate : null;
      let isErrorInDateParams = false;
      if (checkIn == "null" || checkOut == "null") {
        isErrorInDateParams = true;
      } else if (checkIn && checkOut) {
        if (differenceInDays(parseISO(checkOut), parseISO(checkIn)) <= 0 || differenceInDays(new Date(checkIn), new Date()) < 0) {
          isErrorInDateParams = true;
        }
      }
      const adultCount = parseInt(searchParams.get("adultsCount") || "0", 10);
      const petAllowed = (searchParams.get("suitablePet") || "false") === "true";
      dispatch(markBookingStore({
        propertyId: property.property_id,
        adultNumber: adultCount ? adultCount : property.max_no_guests > 1 ? 2 : 1,
        childNumber: parseInt(searchParams.get("childrenCount") || "0", 10),
        petNumber: parseInt(searchParams.get("petNumber") || (petAllowed && property?.max_pet_count && property?.max_pet_count > 0 ? "1" : "0"), 10),
        pet: petAllowed,
        commissionRatePercent,
        ...(checkIn && checkOut ? isErrorInDateParams ? {
          startDate: null,
          endDate: null,
          dayCount: 0,
          error: "Prequote Error"
        } : {
          startDate: formatDateYYYYMMDD(checkIn),
          endDate: formatDateYYYYMMDD(checkOut),
          dayCount: differenceInDays(parseISO(checkOut), parseISO(checkIn))
        } : {
          startDate: null,
          endDate: null,
          dayCount: 0
        })
      }));
    }
  }, [dispatch, property.max_no_guests, property.property_id]);

  // set search parameters from search results
  useEffect(() => {
    const formattedLocationName = ensembleCityStateCountry({
      city: property.city,
      state: property.state,
      country: property.country
    });
    const checkIn = params?.get("check_in") || null;
    const checkOut = params?.get("check_out") || null;
    const noOfDays = checkIn && checkOut ? differenceInDays(parseISO(checkOut), parseISO(checkIn)) : 0;
    const locationName = (params?.get("name")?.trim()?.toLowerCase() !== MAP_AREA ? params?.get("name")?.trim() : "") || formattedLocationName || "";
    dispatch(markSearch({
      longName: dashedToCommaSeparatedName(locationName),
      shortName: dashedToCommaSeparatedName(locationName),
      startDate: checkIn,
      endDate: checkOut,
      noOfDays,
      adultCount: Number(params?.get("adultsCount")) || 2,
      childrenCount: Number(params?.get("childrenCount")) || 0,
      petAllowed: params?.get("suitablePet") ? params?.get("suitablePet") === "true" : false,
      cancellable: params?.get("cancellable") === "true" || false
    }));
  }, [dispatch, property, params]);
  return <></>;
};
export default DetailPageInitiateLogic;